/**
 * Video Cleanup Integration Handler
 * 
 * Handles cleanup operations in VideoEditor without polluting the main component.
 * Follows separation of concerns principle.
 */

import { 
  startVideoCleanup, 
  CleanupConfig 
} from '../../services/videoCleanupService';
import { fetchMediaJsonFromS3 } from './mediaItemService';
import { MediaItem } from '../../types';

// ============================================================================
// Types
// ============================================================================

interface NotificationContext {
  showNotification: (params: {
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }) => void;
}

interface CleanupCallbacks {
  setCleanupJobId: (jobId: string | null) => void;
  setIsCleanupModalOpen: (isOpen: boolean) => void;
  setDisabledBtnPendingDownload: (disabled: boolean) => void;
  setMediaItems: (items: MediaItem[]) => void;
}

// ============================================================================
// Handlers
// ============================================================================

/**
 * Handle starting cleanup process
 * 
 * Backend will process ALL media items in the project:
 * - Extract audio from all video/audio items
 * - Consolidate based on start/end/duration
 * - Transcribe and analyze the complete audio
 * - Create cleaned segments
 */
export async function handleStartCleanup(
  projectId: string, // Changed from mediaItemId
  config: CleanupConfig,
  callbacks: CleanupCallbacks,
  context: NotificationContext
): Promise<void> {
  const { 
    setCleanupJobId, 
    setIsCleanupModalOpen, 
    setDisabledBtnPendingDownload 
  } = callbacks;
  const { showNotification } = context;

  try {
    // Show loading state
    setDisabledBtnPendingDownload(true);
    
    // Start cleanup
    const { jobId } = await startVideoCleanup({
      projectId, // Changed from mediaItemId
      ...config,
    });

    // Update state
    setCleanupJobId(jobId);
    setIsCleanupModalOpen(false);

    // Show success notification
    // showNotification({
    //   type: 'info',
    //   title: 'Cleanup Started',
    //   message: 'AI is analyzing your video. This may take a minute...',
    // });
  } catch (error) {
    // Handle error
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    
    showNotification({
      type: 'error',
      title: 'Cleanup Failed',
      message: `Failed to start cleanup: ${errorMessage}`,
    });
    
    setDisabledBtnPendingDownload(false);
  }
}

/**
 * Handle cleanup completion
 */
export async function handleCleanupComplete(
  projectId: string,
  callbacks: CleanupCallbacks,
  context: NotificationContext
): Promise<void> {
  const { 
    setCleanupJobId, 
    setDisabledBtnPendingDownload, 
    setMediaItems 
  } = callbacks;
  const { showNotification } = context;

  try {
    // Clear job ID
    setCleanupJobId(null);
    
    // Reload media items to show new segments
    const data = await fetchMediaJsonFromS3(projectId);
    
    if (data && Array.isArray(data)) {
      // ✅ Filter out archived items (keep only cleanup segments)
      const activeMedia = data.filter((item: MediaItem) => item.effect !== 'archived');
      
      const loadedMedia = activeMedia.map((mediaItem: MediaItem) => ({
        ...mediaItem,
        id: `${Date.now()}-${Math.floor(Math.random() * 10000)}-${mediaItem.name}`,
      }));
      
      setMediaItems(loadedMedia);
      
      // Calculate stats from active media only
      const totalSegments = loadedMedia.length;
      const keptSegments = loadedMedia.filter(item => 
        item.effect === 'cleanup-kept' || !item.effect
      ).length;
      const removedSegments = loadedMedia.filter(item => 
        item.effect === 'cleanup-removed'
      ).length;

      showNotification({
        type: 'success',
        title: 'Cleanup Complete!',
        message: `Created ${totalSegments} segments. Removed ${removedSegments} filler sections.`,
      });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    
    showNotification({
      type: 'error',
      title: 'Failed to Load Results',
      message: errorMessage,
    });
  } finally {
    // ✅ Re-enable download button
    setDisabledBtnPendingDownload(false);
  }
}

/**
 * Get cleanup statistics
 */
export function getCleanupStats(mediaItems: MediaItem[]): {
  totalSegments: number;
  keptSegments: number;
  removedSegments: number;
  hasCleanup: boolean;
} {
  const cleanupItems = mediaItems.filter(item => 
    item.effect === 'cleanup-kept' || item.effect === 'cleanup-removed'
  );

  return {
    totalSegments: cleanupItems.length,
    keptSegments: cleanupItems.filter(item => item.effect === 'cleanup-kept').length,
    removedSegments: cleanupItems.filter(item => item.effect === 'cleanup-removed').length,
    hasCleanup: cleanupItems.length > 0,
  };
}
